
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ImageUploader } from '@/components/ui/image-uploader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BlogPostFormData } from '@/hooks/blog/useBlogPostForm';
import { Database } from '@/integrations/supabase/types';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { CalendarIcon, Clock } from 'lucide-react';
import { format, parseISO, addHours, setHours, setMinutes } from 'date-fns';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { RichTextEditor } from '@/components/ui/rich-text-editor/RichTextEditor';

type ContentStatus = Database['public']['Enums']['content_status'];

interface BlogContentTabProps {
  formData: BlogPostFormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (name: string, value: ContentStatus) => void;
  handleImageUploaded: (imagePath: string) => void;
  handleDateTimeChange: (dateTime: Date | undefined) => void;
  handleContentChange?: (html: string) => void;
}

export function BlogContentTab({
  formData,
  handleChange,
  handleSelectChange,
  handleImageUploaded,
  handleDateTimeChange,
  handleContentChange
}: BlogContentTabProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    formData.scheduled_at ? parseISO(formData.scheduled_at) : undefined
  );
  
  // Format time as hours and minutes
  const [selectedTime, setSelectedTime] = useState<string>(
    formData.scheduled_at 
      ? format(parseISO(formData.scheduled_at), 'HH:mm') 
      : format(addHours(new Date(), 1), 'HH:mm')
  );

  // Update state when formData changes (e.g., when the initial data loads)
  useEffect(() => {
    if (formData.scheduled_at) {
      setSelectedDate(parseISO(formData.scheduled_at));
      setSelectedTime(format(parseISO(formData.scheduled_at), 'HH:mm'));
    }
  }, [formData.scheduled_at]);
  
  // Handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const dateTime = setMinutes(setHours(date, hours), minutes);
      handleDateTimeChange(dateTime);
    } else {
      handleDateTimeChange(undefined);
    }
  };
  
  // Handle time selection
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTime(e.target.value);
    if (selectedDate) {
      const [hours, minutes] = e.target.value.split(':').map(Number);
      const dateTime = setMinutes(setHours(selectedDate, hours), minutes);
      handleDateTimeChange(dateTime);
    }
  };
  
  // Clear scheduled date and time
  const handleClearSchedule = () => {
    setSelectedDate(undefined);
    setSelectedTime(format(addHours(new Date(), 1), 'HH:mm'));
    handleDateTimeChange(undefined);
  };

  // Handle rich text content changes with guaranteed change tracking
  const onRichTextChange = (html: string) => {
    console.log("Rich text content changed in BlogContentTab, length:", html.length);
    console.log("Rich text sample:", html.substring(0, 50) + (html.length > 50 ? '...' : ''));
    
    if (handleContentChange) {
      console.log("Using dedicated handleContentChange for rich text");
      // Always mark as changed, even if content looks the same
      handleContentChange(html);
    } else {
      // Fallback for backwards compatibility
      console.log("Using legacy handleChange for content (no handleContentChange provided)");
      const syntheticEvent = {
        target: {
          name: 'content',
          value: html
        }
      } as React.ChangeEvent<HTMLTextAreaElement>;
      
      handleChange(syntheticEvent);
    }
  };

  // Log current image state for debugging
  useEffect(() => {
    console.log("Current featured image in BlogContentTab:", formData.featured_image);
  }, [formData.featured_image]);
  
  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle>Blog Information</CardTitle>
        <CardDescription>
          Enter the information for your blog post
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input 
            id="title" 
            name="title" 
            placeholder="My Amazing Blog Post" 
            value={formData.title}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="slug">Slug *</Label>
          <Input 
            id="slug" 
            name="slug" 
            placeholder="my-amazing-blog-post" 
            value={formData.slug}
            onChange={handleChange}
            required
          />
          <p className="text-xs text-muted-foreground">
            This will be used in the URL. Use only lowercase letters, numbers, and hyphens.
          </p>
        </div>
        
        <ImageUploader 
          onImageUploaded={handleImageUploaded}
          currentImage={formData.featured_image}
          label="Featured Image"
          aspectRatio={16/9}
          maxWidth={1200}
          maxHeight={675}
          maxFileSizeMB={5}
        />
        
        <div className="space-y-2">
          <Label htmlFor="excerpt">Excerpt</Label>
          <Textarea 
            id="excerpt" 
            name="excerpt" 
            placeholder="A brief summary of your post" 
            value={formData.excerpt}
            onChange={handleChange}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="content">Content</Label>
          <RichTextEditor
            value={formData.content}
            onChange={onRichTextChange}
            placeholder="Write your blog post content here..."
            minHeight={300}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select 
            value={formData.status} 
            onValueChange={(value) => handleSelectChange('status', value as ContentStatus)}
          >
            <SelectTrigger id="status">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Scheduling controls */}
        <div className="space-y-2 border-t pt-4">
          <Label>Schedule Publication</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-normal w-full sm:w-[240px]",
                    !selectedDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={(date) => date < new Date() || date < new Date("1900-01-01")}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            
            <div className="flex items-center gap-2">
              <div className="relative">
                <Clock className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="time"
                  value={selectedTime}
                  onChange={handleTimeChange}
                  className="pl-8"
                  disabled={!selectedDate}
                />
              </div>
              
              {selectedDate && (
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={handleClearSchedule}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          {formData.status === 'scheduled' && selectedDate && formData.scheduled_at && (
            <p className="text-sm text-muted-foreground">
              This post will be automatically published on {format(parseISO(formData.scheduled_at), "PPP 'at' h:mm a")}.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
